// components/home/listings/property/PropertyCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
    FaMapMarkerAlt, FaBed, FaLayerGroup, FaRulerCombined, FaArrowRight,
    FaHandPointer
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import propertyImagePlaceholder from '../../../../assets/hoteldefault.avif';
import './PropertyCard.css';

/* URL d'image fiable : backslashes Windows normalisés, URLs complètes passées telles quelles */
const buildImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const normalized = image.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${normalized}`;
};

const SlickSlider = Slider.default ? Slider.default : Slider;

const PropertyCard = ({ id, files, name, location, rooms, floors, area, price, mode = 'rent', isFirst = false, hintDelay = 500 }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const sliderRef = useRef(null);
    const images = (files || []).map(f => buildImageUrl(f?.url)).filter(Boolean);
    const hasMultipleImages = images.length > 1;

    /* Indice de première visite : la main ne s'affiche que sur la PREMIÈRE carte,
       une seule fois par navigateur (localStorage). */
    useEffect(() => {
        if (!hasMultipleImages || !isFirst) return;
        if (localStorage.getItem('spotfli_image_hint_shown')) return;
        const timer = setTimeout(() => {
            setShowHint(true);
            localStorage.setItem('spotfli_image_hint_shown', '1');
        }, hintDelay);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Cliquer sur l'image → photo suivante */
    const handleImageClick = () => {
        if (hasMultipleImages && sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    const handleOpenDetails = () => {
        navigate(`/propertyDetails/${id}`);
    };

    const sliderSettings = {
        dots: true,
        infinite: hasMultipleImages,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        beforeChange: (current, next) => setCurrentSlide(next)
    };

    const handleImgError = (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = propertyImagePlaceholder;
    };

    const formatPrice = (p) => {
        if (!p || Number(p) === 0) return null;
        return `XAF ${Number(p).toLocaleString()}`;
    };

    const formattedPrice = formatPrice(price);
    const badgeLabel = mode === 'sale' ? t('property_page.for_sale') : t('property_page.for_rent');
    const detailRooms = rooms || 0;

    return (
        <div className="property-card">
            <div className="property-image-slider">
                {hasMultipleImages ? (
                    <SlickSlider ref={sliderRef} {...sliderSettings}>
                        {images.map((image, index) => (
                            <div key={index} className="property-slide clickable" onClick={handleImageClick}>
                                <img
                                    src={image}
                                    alt={`${name} - ${index + 1}`}
                                    onError={handleImgError}
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </SlickSlider>
                ) : images.length === 1 ? (
                    <div className="property-slide">
                        <img src={images[0]} alt={name} onError={handleImgError} loading="lazy" />
                    </div>
                ) : (
                    <div className="no-image">
                        <img src={propertyImagePlaceholder} alt={name || t('property_page.no_name')} loading="lazy" />
                    </div>
                )}

                {/* Badge For Rent / For Sale */}
                <span className={`property-badge ${mode === 'sale' ? 'sale' : ''}`}>{badgeLabel}</span>

                {hasMultipleImages && (
                    <div className="property-slide-count">
                        {currentSlide + 1}/{images.length}
                    </div>
                )}

                {/* Indice première visite : une main tape l'image */}
                {showHint && hasMultipleImages && (
                    <div
                        className="property-tap-hint"
                        role="status"
                        aria-label={t('property_page.click_images_hint')}
                    >
                        <FaHandPointer className="tap-hand" />
                        <span className="tap-ripple" aria-hidden="true" />
                    </div>
                )}
            </div>

            <div className="property-info">
                <div className="property-header">
                    <h3 className="property-name">{name || t('property_page.no_name')}</h3>
                </div>

                <div className="property-location">
                    <FaMapMarkerAlt className="location-icon" />
                    <span>{location || t('property_page.location_not_specified')}</span>
                </div>

                <div className="property-details">
                    {detailRooms > 0 && (
                        <div className="property-detail-item">
                            <FaBed className="detail-icon" />
                            <span>{detailRooms} {mode === 'rent' ? t('property_page.apartments') : t('property_page.rooms')}</span>
                        </div>
                    )}
                    {floors > 0 && (
                        <div className="property-detail-item">
                            <FaLayerGroup className="detail-icon" />
                            <span>{floors} {floors === 1 ? t('property_page.floor') : t('property_page.floors')}</span>
                        </div>
                    )}
                    {area > 0 && (
                        <div className="property-detail-item">
                            <FaRulerCombined className="detail-icon" />
                            <span>{area} m²</span>
                        </div>
                    )}
                </div>

                <div className="property-price-row">
                    {formattedPrice ? (
                        <>
                            <span className="price-amount">{formattedPrice}</span>
                            {mode === 'rent' && <span className="price-night">{t('property_page.per_month')}</span>}
                        </>
                    ) : (
                        <span className="price-on-request">{t('property_page.price_on_request')}</span>
                    )}
                </div>

                <button className="view-property-btn" onClick={handleOpenDetails}>
                    <span>{t('property_page.view_details')}</span>
                    <FaArrowRight className="view-property-arrow" />
                </button>
            </div>
        </div>
    );
};

export default PropertyCard;
